import { IconChevronDown } from '@tabler/icons-react';
import { Button } from '@zorro/design-system/v2/Button';
import { Menu } from '@zorro/design-system/v2/Menu';
import { useRoles } from '@zorro/web/ui-utils';
import { useState } from 'react';

import { AccountManagementSection } from './AccountManagement/AccountManagementSection';
import { EmailCommunicationsSection } from './EmailCommunications/EmailCommunicationsSection';
import { ActionsMenuSection } from './EmployeeActionsUtils';
import { EmployeeAdministrationSection } from './EmployeeAdministration/EmployeeAdministrationSection';
import { EnrollmentActionsSection } from './EnrollmentActions/EnrollmentActionsSection';

type ActionMenuProps = {
  recordsForActions: ActionsMenuSection['recordsForActions'];
  setOverlayId: ActionsMenuSection['setOverlayId'];
  employerId?: string;
  isInEnrollmentsDatatable?: boolean;
};

export function ActionsMenu({
  recordsForActions,
  setOverlayId,
  employerId,
  isInEnrollmentsDatatable = false,
}: ActionMenuProps) {
  const { isManagement } = useRoles();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const selectedEmployeesCount = recordsForActions.length;

  const uniqueEnrollmentPeriodIds = [
    ...new Set(recordsForActions.map((record) => record.enrollmentPeriodId)),
  ];
  const uniqueEmployerIds = [
    ...new Set(recordsForActions.map((record) => record.employerId)),
  ];
  const uniqueEnrollmentTeamIds = [
    ...new Set(recordsForActions.map((record) => record.enrollmentTeamId)),
  ];

  const isAccountManagementSectionEmpty =
    isInEnrollmentsDatatable && selectedEmployeesCount > 1;

  return (
    <Menu
      opened={isMenuOpen}
      onChange={setIsMenuOpen}
      size="md"
      disabled={selectedEmployeesCount === 0}
    >
      <Menu.Target>
        <Button
          disabled={selectedEmployeesCount === 0}
          variant="primary"
          size="sm"
          rightSection={
            <IconChevronDown width="1.1875rem" height="1.1875rem" />
          }
        >
          Actions
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <EmailCommunicationsSection
          setOverlayId={setOverlayId}
          recordsForActions={recordsForActions}
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        />

        {isManagement && (
          <EnrollmentActionsSection
            setOverlayId={setOverlayId}
            closeMenu={() => setIsMenuOpen(false)}
            recordsForActions={recordsForActions}
            isSingleEnrollmentPeriod={uniqueEnrollmentPeriodIds.length === 1}
            isSingleEmployer={uniqueEmployerIds.length === 1}
            isSingleEnrollmentTeam={uniqueEnrollmentTeamIds.length === 1}
            isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          />
        )}

        <EmployeeAdministrationSection
          setOverlayId={setOverlayId}
          recordsForActions={recordsForActions}
          employerId={
            employerId ||
            (uniqueEmployerIds.length === 1 ? uniqueEmployerIds[0] : undefined)
          }
          isInEnrollmentsDatatable={isInEnrollmentsDatatable}
        />

        {!isAccountManagementSectionEmpty && (
          <AccountManagementSection
            setOverlayId={setOverlayId}
            recordsForActions={recordsForActions}
            isInEnrollmentsDatatable={isInEnrollmentsDatatable}
          />
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
